import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Trophy, Users, Star, TrendingUp, Moon, Sun } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { useEffect } from 'react';

const features = [
  {
    icon: Trophy,
    title: 'Classifica in tempo reale',
    description: 'Segui i punteggi e scala la classifica della tua lega',
  },
  {
    icon: Users,
    title: 'Gioca in coppia o da solo',
    description: 'Unisci le forze con un compagno o competi singolarmente',
  },
  {
    icon: Star,
    title: 'Bonus e Malus',
    description: 'Sistema di punteggio basato sul comportamento dei prof',
  },
  {
    icon: TrendingUp,
    title: 'Gestisci la tua lega',
    description: 'Crea leghe, invita amici e diventa admin',
  },
];

export const Landing = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, user, isLoading } = useAuth();

  // Automatic redirect to /leagues when user is authenticated
  // This is triggered after Google OAuth completes and does full page reload to root
  useEffect(() => {
    console.log('🏠 [Landing] Componente montato');
    console.log('🏠 [Landing] isLoading:', isLoading);
    console.log('🏠 [Landing] isAuthenticated:', isAuthenticated);
    console.log('🏠 [Landing] user:', user);
    
    // Only redirect when loading is complete and user is authenticated
    if (!isLoading && isAuthenticated && user) {
      console.log('✅ [Landing] Utente autenticato e caricato, redirect a /leagues');
      navigate('/dashboard', { replace: true });
    } else if (!isLoading && !isAuthenticated) {
      console.log('❌ [Landing] Utente NON autenticato, rimango sulla landing');
    } else {
      console.log('⏳ [Landing] Ancora in loading...');
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  return (
    <div className="min-h-screen bg-background">
      {/* DEBUG BANNER - Solo se vieni da Google OAuth */}
      {window.location.search.includes('from_google') && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white p-4 text-center font-bold">
          🐛 DEBUG: SEI SULLA LANDING PAGE dopo Google Login! isLoading: {isLoading ? 'TRUE' : 'FALSE'}, isAuthenticated: {isAuthenticated ? 'TRUE' : 'FALSE'}, user: {user ? user.email : 'NULL'}
        </div>
      )}
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b">
        <div className="flex items-center justify-between h-14 px-4 max-w-5xl mx-auto">
          <h1 
            className="text-xl font-bold tracking-tight text-primary" 
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="landing-logo"
          >
            FANTACLASSE
          </h1>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="landing-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              data-testid="landing-login-btn"
            >
              Accedi
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-md mx-auto text-center md:max-w-2xl">
          <div className="mb-6">
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
              Il Fantasy Game della scuola
            </span>
          </div>
          
          <h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="landing-title"
          >
            Trasforma i tuoi{' '}
            <span className="text-primary">professori</span>{' '}
            in campioni
          </h2>
          
          <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
            Unisciti ad una lega, scegli i tuoi professori e accumula punti 
            in base a quello che succede in classe. Chi avrà il team migliore?
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="rounded-full px-8 shadow-lg shadow-primary/20 btn-press"
              onClick={() => navigate('/register')}
              data-testid="landing-register-btn"
            >
              Inizia gratis
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full px-8 btn-press"
              onClick={() => navigate('/login')}
              data-testid="landing-join-btn"
            >
              Ho già un account
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 bg-secondary/30">
        <div className="max-w-5xl mx-auto">
          <h3 
            className="text-2xl font-bold text-center mb-12"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Come funziona
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger-children">
            {features.map(({ icon: Icon, title, description }, index) => (
              <div
                key={title}
                className="p-6 rounded-xl bg-card border shadow-sm hover-lift"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">{title}</h4>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-md mx-auto text-center">
          <h3 
            className="text-2xl font-bold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Pronto a giocare?
          </h3>
          <p className="text-muted-foreground mb-8">
            Crea la tua lega o unisciti ad una esistente. È gratis!
          </p>
          <Button
            size="lg"
            className="rounded-full px-8 shadow-lg shadow-primary/20 btn-press"
            onClick={() => navigate('/register')}
            data-testid="landing-cta-btn"
          >
            Registrati ora
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-5xl mx-auto text-center text-sm text-muted-foreground">
          <p>FantaClasse - Il Fantasy Game per la scuola</p>
        </div>
      </footer>
    </div>
  );
};
